import { createContext, FC, ReactNode, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { SnowflakeDataSharingService } from "@app/operations/snowflake-data-sharing/snowflakeDataSharingService";
import { ClientDataShareSummary } from "@app/operations/snowflake-data-sharing/interfaces";
import { Tabs } from "@app/operations/snowflake-data-sharing/enums";

interface QueryStatus {
  isError: boolean;
  isLoading: boolean;
}

interface SnowflakeDataSharingContextProps {
  getClients: () => ClientDataShareSummary[] | undefined;
  getClientsWithAccess: () => number | undefined;
  getConfiguredClients: () => number | undefined;
  getProjectCount: () => number | undefined;
  getClientCount: () => number | undefined;
  getUnresolvedFailures: () => number | undefined;
  getLastRefreshTime: () => string | undefined;
  getTotalProjectsInDataShare: () => number | undefined;
  getNextBatchTime: () => string | undefined;
  getRefreshCadence: () => string | undefined;
  isTabLoading: (tab: Tabs) => boolean;
  isTabError: (tab: Tabs) => boolean;
}

const SnowflakeDataSharingContext = createContext<Partial<SnowflakeDataSharingContextProps>>({});

const getNextHourlyBatchTime = (): string => {
  const nextBatchTime = new Date();
  nextBatchTime.setUTCHours(nextBatchTime.getUTCHours() + 1, 0, 0, 0);
  return nextBatchTime.toISOString();
};

const SnowflakeDataSharingContextProvider: FC<{ children: ReactNode }> = (props: { children: ReactNode }) => {

  const dataSharingService = useRef(new SnowflakeDataSharingService()).current;

  const clientsQuery = useQuery({
    queryFn: ({ signal }) => dataSharingService.getDataShareClientDashboard(signal),
    queryKey: ["snowflake-data-sharing-clients"],
  });

  const projectsInNextRunQuery = useQuery({
    queryFn: () => dataSharingService.getProjectsInNextRun(),
    queryKey: ["snowflake-data-sharing-projects-in-next-run"],
  });

  const refreshSummaryQuery = useQuery({
    queryFn: () => dataSharingService.getRefreshSummary(),
    queryKey: ["snowflake-data-sharing-refresh-summary"],
  });

  const tabQueries: Record<Tabs, QueryStatus[]> = {
    [Tabs.Dashboard]: [clientsQuery, projectsInNextRunQuery, refreshSummaryQuery],
    [Tabs.SharingRunMonitor]: [],
    [Tabs.SnowflakeIntegration]: [clientsQuery],
  };

  const contextValue: Partial<SnowflakeDataSharingContextProps> = {
    getClientCount: () => projectsInNextRunQuery.data?.clientCount,
    getClients: () => clientsQuery.data,
    getClientsWithAccess: () => clientsQuery.data?.filter((client) => client.isDataShareActive).length,
    getConfiguredClients: () => clientsQuery.data?.length,
    getLastRefreshTime: () => refreshSummaryQuery.data?.lastRefreshTime,
    getNextBatchTime: () => getNextHourlyBatchTime(),
    getProjectCount: () => projectsInNextRunQuery.data?.projectCount,
    getRefreshCadence: () => "hourly",
    getTotalProjectsInDataShare: () => refreshSummaryQuery.data?.totalProjectsInDataShare,
    getUnresolvedFailures: () => refreshSummaryQuery.data?.unresolvedFailures,
    isTabError: (tab) => tabQueries[tab].some((query) => query.isError),
    isTabLoading: (tab) => tabQueries[tab].some((query) => query.isLoading),
  };

  return (
    <SnowflakeDataSharingContext value={contextValue}>
      {props.children}
    </SnowflakeDataSharingContext>
  );
};

export { SnowflakeDataSharingContext, SnowflakeDataSharingContextProvider, SnowflakeDataSharingContextProps };
